// Study Profile — Chrona Review feature (A2F-1361/1445, un-gated).
//
// One table per study. Each row is a feature; it stays quiet when sources agree
// and draws the eye when one likely drifted from its authoritative source. Renders
// the A2F-1366 backend contract (GET /study-profile) — the engine decides, the UI
// renders. Implements the Study screen of Review Interaction Model.dc.html.
//
// Five render states fed by real contract rows:
//   quiet        — one consistent value across sources
//   blank        — not stated by any source
//   authority    — a directed drift (suspect → authoritative source); identifier (E7)
//   equivalent   — design-field values differ, NON-directional (see deferral note below)
//   unextractable+pendingOwner — PENDING: enrollment/endpoint (E5/E6), not wired until A2F-1299
//
// Dismiss/restore are server-persisted + audited, against the SHARED reconciliation
// finding store (A2F-1222) — one finding, one dismissal across surfaces. The GET
// read-path merges dismissal state back onto each row (`row.dismissed`).
//
// NOTE (parked product question): design-field drift (e.g. Blinding open-label vs
// double-blind) renders here as a non-directional `equivalent` difference. Authoritative-
// source DIRECTION for design fields (the design draws Blinding as authority→source) is
// NOT emitted by A2F-1366 — authority direction is the A2F-1351 numeric/identifier path,
// not the design-extractor path. Deferred; surfaced in-page so it's parked, not lost.

import { useCallback, useEffect, useState } from 'react';
import {
  TriangleAlert, ArrowRight, Check, Clock, EyeOff, ChevronDown, RefreshCw,
  RotateCcw, Shield, Lock, LoaderCircle, FlaskConical, FileQuestion, X, ExternalLink,
} from 'lucide-react';

import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { api } from '@/lib/api';
import { useScanTask } from '@/lib/useScanTask';
import { useSubmissionFilter } from '@/lib/useSubmissionFilter';
import { ScanFooter } from '@/components/shared/ScanFooter';
import type {
  StudyProfileResponse,
  StudyProfileEntity,
  StudyProfileRow,
  StudyProfileEvidence,
  StudyProfileCoverage,
} from '@/types/api';

const MIN_RATIONALE = 10;

// Verdict → pill treatment for an evidence row (the design's pill() kinds).
function verdictPill(verdict: string): string {
  const base = 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap';
  if (/source|authoritative/i.test(verdict)) return `${base} bg-white border border-brand-charcoal text-brand-charcoal`;
  if (/drift|discrepan/i.test(verdict)) return `${base} bg-amber-50 border border-amber-200 text-amber-700`;
  if (/unreadable|unextractable/i.test(verdict)) return `${base} bg-white border border-dashed border-slate-300 text-slate-600`;
  return `${base} bg-white border border-slate-200 text-slate-500`;
}

function verdictLabel(verdict: string): string {
  const map: Record<string, string> = {
    authoritative_source: 'authoritative source', authoritative: 'authoritative source',
    drifted: 'drifted', consistent: 'consistent', unattributed: 'unattributed',
    blank: 'not stated', unextractable: 'could not read',
  };
  return map[verdict] ?? verdict.replace(/_/g, ' ');
}

// --- one evidence record (expanded view): doc + version pin + verbatim.
// (No eCTD module badge — the document name carries its section, so the module is
// already evident; the badge was redundant.)
function EvidenceItem({ ev }: { ev: StudyProfileEvidence }) {
  return (
    <div className="py-2.5 border-t border-slate-100">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {ev.docId ? (
            <button
              type="button"
              onClick={() => { void api.documents.openLocal(String(ev.docId)).catch(() => {}); }}
              title="Open in Word"
              className="group inline-flex items-center gap-1 text-[13px] font-semibold text-brand-orange hover:underline truncate"
            >
              <span className="truncate">{ev.docName}</span>
              <ExternalLink className="w-3 h-3 shrink-0 opacity-70 group-hover:opacity-100" />
            </button>
          ) : (
            <span className="text-[13px] font-semibold text-brand-charcoal truncate">{ev.docName}</span>
          )}
          {ev.versionLabel && (
            <span className="font-mono text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-px">
              {ev.versionLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-none">
          {ev.state === 'stated' && <ConfidenceBadge confidence={ev.confidence} />}
          <span className={verdictPill(ev.verdict)}>{verdictLabel(ev.verdict)}</span>
        </div>
      </div>

      {/* verbatim (stated) */}
      {ev.state === 'stated' && ev.verbatim && (
        <div className="mt-1.5 pl-3 border-l-2 border-slate-200 text-[13px] leading-relaxed text-slate-600">
          <span className="text-slate-400">&ldquo;</span>
          {ev.verbatim.before}
          <span className="rounded px-1 font-mono font-semibold bg-orange-50 text-amber-800">{ev.verbatim.match}</span>
          {ev.verbatim.after}
          <span className="text-slate-400">&rdquo;</span>
        </div>
      )}

      {/* blank evidence — document doesn't state this (normal) */}
      {ev.state === 'blank' && (
        <div className="mt-1.5 flex items-center gap-2 text-[12.5px] text-slate-500">
          <span className="text-base text-slate-300 leading-none">—</span>
          Document does not state this. <span className="text-[11px] text-slate-400">(blank — normal)</span>
        </div>
      )}

      {/* unextractable evidence — could not read (distinct from blank, A2F-1332) */}
      {ev.state === 'unextractable' && (
        <div className="mt-1.5 flex items-center gap-2 px-2.5 py-2 rounded-md border border-dashed border-slate-300 bg-slate-50 text-[12.5px] font-medium text-slate-600">
          <EyeOff className="w-3.5 h-3.5 shrink-0" />
          {ev.unextractableReason || 'Chrona could not read this document. This is not blank — the value may be stated here.'}
        </div>
      )}
    </div>
  );
}

// --- modality label (A2F-1381/1382): a reviewer-facing tag (planned/actual), NEVER a gate. ----
function ModalityTag({ modality }: { modality?: string | null }) {
  if (!modality) return null;
  return (
    <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400 align-middle">
      {modality}
    </span>
  );
}

// --- per-document confidence badge (A2F-1382): high | medium | low, where the source gives it. -
function ConfidenceBadge({ confidence }: { confidence?: string | null }) {
  const c = (confidence || '').toLowerCase();
  if (c !== 'high' && c !== 'medium' && c !== 'low') return null;
  const cls = c === 'high' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : c === 'low' ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-slate-500 bg-slate-50 border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-semibold border ${cls}`}
      title="Extraction confidence for this document">
      {c} confidence
    </span>
  );
}

// --- value-chip atoms --------------------------------------------------------
function QuietChip({ value, count, modality }: { value: string; count: number; modality?: string | null }) {
  return (
    <span className="inline-flex items-center font-mono text-[13px] font-medium bg-slate-50 border border-slate-200 text-slate-700 rounded-md px-2.5 py-1">
      {value}
      {count > 1 && <span className="text-slate-400 font-semibold text-[11px] ml-1.5">×{count}</span>}
      <ModalityTag modality={modality} />
    </span>
  );
}

function DriftChip({ value, count, tone, modality }: { value: string; count: number; tone: 'drift' | 'source'; modality?: string | null }) {
  const cls = tone === 'source'
    ? 'bg-white border border-brand-charcoal text-brand-charcoal'
    : 'bg-amber-50 border border-amber-200 text-amber-700';
  return (
    <span className={`inline-flex items-center font-mono text-[13px] font-semibold rounded-md px-2.5 py-1 whitespace-nowrap ${cls}`}>
      {value}
      {count > 1 && <span className="opacity-60 font-semibold text-[11px] ml-1.5">×{count}</span>}
      <ModalityTag modality={modality} />
    </span>
  );
}

// --- coverage tail (A2F-1382): a `single`-source value is NOT a confident clean — it can't be
// cross-confirmed, so it reads as a muted caution, distinct from the green corroborated tick.
function ConsistentTail({ coverage }: { coverage?: StudyProfileCoverage | null }) {
  if (coverage?.state === 'single') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500"
        title="Only one document states this — it cannot be cross-confirmed.">
        <span className="w-[7px] h-[7px] rounded-full border border-slate-400" /> 1 source
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
      <span className="w-[7px] h-[7px] rounded-full bg-emerald-500" />
      consistent{coverage && coverage.stated > 0 ? ` · ${coverage.stated} sources` : ''}
    </span>
  );
}

// --- the chip area + tail verdict for one row, by render state ---------------
function RowBody({ row }: { row: StudyProfileRow }) {
  // PENDING (Option A, A2F-1366) — render="unextractable" + pendingOwner. A distinct
  // fourth empty-ish state: reads "coming, not yet wired", never blank, never a finding.
  if (row.render === 'unextractable' && row.pendingOwner) {
    return (
      <>
        <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-dashed border-slate-300 bg-slate-50 text-[10.5px] font-bold uppercase tracking-wider text-slate-500">
            <Clock className="w-3 h-3" /> pending
          </span>
          <span className="text-[12.5px] text-slate-500">
            {row.label} consistency isn&apos;t wired yet — coming with {row.pendingOwner}.
          </span>
        </div>
        <div className="flex-none flex items-center gap-3 pt-px">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
            pending · {row.pendingOwner}
          </span>
        </div>
      </>
    );
  }

  // tail verdict + chip area per render state
  let chips: React.ReactNode = null;
  let tail: React.ReactNode = null;

  if (row.render === 'blank') {
    chips = <span className="text-lg text-slate-300 leading-none">—</span>;
    tail = <span className="text-[11px] font-semibold text-slate-400">not stated by any source</span>;
  } else if (row.render === 'quiet') {
    const v = row.variations[0];
    chips = v ? <QuietChip value={v.value} count={v.docCount} modality={v.modality} /> : <span className="text-slate-300">—</span>;
    tail = <ConsistentTail coverage={row.coverage} />;
  } else if (row.render === 'authority') {
    const auth = row.authority;
    const suspects = row.variations.filter((v) => v.value !== auth?.authoritativeValue);
    chips = (
      <div className="flex items-center gap-2.5 flex-wrap">
        {(suspects.length ? suspects : row.variations).map((v, i) => (
          <DriftChip key={i} value={v.value} count={v.docCount} tone="drift" modality={v.modality} />
        ))}
        <span className="inline-flex items-center text-amber-600">
          <ArrowRight className="w-4 h-4" />
        </span>
        {auth?.authoritativeValue && <DriftChip value={auth.authoritativeValue} count={0} tone="source" />}
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">source</span>
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
        <TriangleAlert className="w-3 h-3" /> likely drift
      </span>
    );
  } else if (row.render === 'equivalent') {
    // Design-field values differ, NON-directional (see file-header deferral note).
    chips = (
      <div className="flex items-center gap-2 flex-wrap">
        {row.variations.map((v, i) => (
          <DriftChip key={i} value={v.value} count={v.docCount} tone="drift" modality={v.modality} />
        ))}
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
        <TriangleAlert className="w-3 h-3" /> differs across sources
      </span>
    );
  } else if (row.render === 'equated') {
    // A2F-1453: values are worded differently but mean the same (e.g. Age "18+" ≡ "18 years and
    // older"). Not a drift, not attention — show the wordings quietly with an amber dot + the word
    // "equivalent" (the sibling of the green "consistent" dot).
    chips = (
      <div className="flex items-center gap-2 flex-wrap">
        {row.variations.map((v, i) => (
          <QuietChip key={i} value={v.value} count={v.docCount} modality={v.modality} />
        ))}
      </div>
    );
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400"
        title="These sources use different wording for the same requirement.">
        <span className="w-[7px] h-[7px] rounded-full bg-amber-400" />
        equivalent{row.coverage && row.coverage.stated > 0 ? ` · ${row.coverage.stated} sources` : ''}
      </span>
    );
  } else {
    // Defensive: render="unextractable" with no pendingOwner isn't expected from the
    // contract (pending rows always carry an owner), but never leave a silent empty
    // gap — show an explicit "unavailable" rather than two blank divs.
    chips = <span className="text-[12.5px] text-slate-400">Not available</span>;
    tail = (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
        <EyeOff className="w-3 h-3" /> unavailable
      </span>
    );
  }

  return (
    <>
      <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">{chips}</div>
      <div className="flex-none flex items-center gap-3 pt-px">{tail}</div>
    </>
  );
}

// --- one feature row (collapsible) -------------------------------------------
const FLAGGED_RENDERS = new Set(['authority', 'equivalent']);

function FeatureRow({
  row, expanded, onToggle, canDismiss, onDismiss, onRestore,
}: {
  row: StudyProfileRow;
  expanded: boolean;
  onToggle: () => void;
  canDismiss: boolean;
  onDismiss: (key: string) => void;
  onRestore: (key: string) => void;
}) {
  const flagged = FLAGGED_RENDERS.has(row.render) && !row.dismissed;
  return (
    <div>
      <div
        className={`feat-row flex items-start gap-4 py-3.5 cursor-pointer border-b border-slate-100 hover:bg-[#FCFBFA] transition-colors ${
          flagged ? 'border-l-2 border-l-brand-orange pl-[18px] pr-5' : 'px-5'
        }`}
        onClick={onToggle}
      >
        <div className="w-[138px] flex-none text-[13px] font-semibold text-brand-charcoal pt-0.5">
          {row.label}
          {row.dismissed && (
            <span className="block text-[10px] font-medium text-slate-400 mt-0.5">dismissed</span>
          )}
        </div>
        <RowBody row={row} />
        <ChevronDown className={`w-[15px] h-[15px] text-slate-400 transition-transform flex-none mt-0.5 ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="px-6 pb-5 pl-[158px] bg-[#FCFBFA] border-b border-slate-100">
          {row.authority?.reason && (
            <p className="text-[12px] text-slate-500 mt-3 leading-relaxed">{row.authority.reason}</p>
          )}
          {row.evidence.length === 0 && (
            <p className="text-[12px] text-slate-400 italic mt-3">No per-document evidence for this feature.</p>
          )}
          {row.evidence.map((ev, i) => <EvidenceItem key={i} ev={ev} />)}

          {/* dismiss / restore (only for dismissable drift rows) */}
          {row.findingKey && (
            <div className="mt-3.5 flex items-center gap-2">
              {row.dismissed ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRestore(row.findingKey!); }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </button>
              ) : canDismiss ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDismiss(row.findingKey!); }}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-white"
                >
                  <Shield className="w-3.5 h-3.5" /> Dismiss with rationale
                </button>
              ) : null}
              {row.dismissed && row.dismissReason && (
                <span className="text-[11px] text-slate-400 italic">&ldquo;{row.dismissReason}&rdquo;</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- one study entity card ---------------------------------------------------
function StudyCard({
  entity, expanded, toggleRow, canDismiss, onDismiss, onRestore,
}: {
  entity: StudyProfileEntity;
  expanded: Record<string, boolean>;
  toggleRow: (id: string) => void;
  canDismiss: boolean;
  onDismiss: (key: string) => void;
  onRestore: (key: string) => void;
}) {
  // Consistent (quiet/blank) rows are SHOWN by default so the reviewer sees the whole
  // picture; the "Hide consistent" toggle stays available to collapse them to just the
  // flagged + pending rows.
  const [showConsistent, setShowConsistent] = useState(true);
  const isConsistent = (r: StudyProfileRow) =>
    (r.render === 'quiet' || r.render === 'blank') && !r.dismissed;
  // A2F-1382: a quiet row that rests on a SINGLE source is consistent-but-single-source — the
  // aggregate roll-ups must not telegraph a confident "all consistent" (it would re-assert at the
  // summary the exact confident-clean the per-row caution withholds).
  const isSingleSource = (r: StudyProfileRow) =>
    r.render === 'quiet' && r.coverage?.state === 'single' && !r.dismissed;
  // A2F-1380: hide pending rows (e.g. Endpoint, A2F-1375). A "coming soon" line on every study
  // card is noise — surface the feature when it actually computes, not before.
  const rows = entity.rows.filter((r) => !(r.render === 'unextractable' && r.pendingOwner));
  const consistentCount = rows.filter(isConsistent).length;
  const singleSourceCount = rows.filter(isSingleSource).length;
  // A2F-1453: rows whose sources agree in meaning but differ in wording (e.g. Age). Not a drift,
  // so the card stays "All consistent" — but call them out so the amber "equivalent" row isn't a
  // surprise under a green header.
  const equivalentCount = rows.filter((r) => r.render === 'equated' && !r.dismissed).length;
  const visibleRows = rows.filter((r) => showConsistent || !isConsistent(r));
  const attention = entity.status === 'attention';
  // Compose the consistent-header suffix: "· N single-source · M equivalent" (whichever apply).
  const consistentParts = [
    singleSourceCount > 0 ? `${singleSourceCount} single-source` : null,
    equivalentCount > 0 ? `${equivalentCount} equivalent` : null,
  ].filter(Boolean);
  const consistentLabel = consistentParts.length
    ? `All consistent · ${consistentParts.join(' · ')}` : 'All consistent';

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-none ${
            attention ? 'bg-amber-500' : singleSourceCount > 0 ? 'border border-slate-400' : 'bg-emerald-500'
          }`} />
          <div className="min-w-0">
            <div className="font-bold text-[15px] text-brand-charcoal truncate">{entity.title}</div>
            {entity.subtitle && <div className="text-[11px] text-slate-500 mt-px">{entity.subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3.5 flex-none">
          <span className={`text-[11px] font-semibold ${
            attention ? 'text-amber-700' : singleSourceCount > 0 ? 'text-slate-500' : 'text-emerald-700'
          }`}>
            {attention ? 'Needs attention' : consistentLabel}
          </span>
          {consistentCount > 0 && (
            <button
              type="button"
              onClick={() => setShowConsistent((s) => !s)}
              className="text-[11px] font-semibold text-brand-orange bg-transparent border-0 py-1"
            >
              {showConsistent ? 'Hide consistent' : `Show ${consistentCount} consistent`}
            </button>
          )}
        </div>
      </div>

      {visibleRows.length === 0 ? (
        <div className="px-5 py-5 flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full flex-none ${singleSourceCount > 0 ? 'border border-slate-400' : 'bg-emerald-500'}`} />
          <span className="text-[13px] text-slate-500">
            {singleSourceCount > 0
              ? `All rows quiet — ${singleSourceCount} rest on a single source.`
              : 'All features consistent across sources.'}
          </span>
        </div>
      ) : (
        visibleRows.map((row) => {
          const id = `${entity.studyId}.${row.feature}`;
          return (
            <FeatureRow
              key={id}
              row={row}
              expanded={!!expanded[id]}
              onToggle={() => toggleRow(id)}
              canDismiss={canDismiss}
              onDismiss={onDismiss}
              onRestore={onRestore}
            />
          );
        })
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function StudyProfilePage() {
  const { activeProject } = useProject();
  const { canRunScans, canDismissFindings } = usePermissions();
  const projectId = activeProject?.id;
  // A2F: Study Profile honors the global submission focus on both read and scan.
  // `submissionId` is null when viewing the full submission (all serials).
  const { submissionId } = useSubmissionFilter();
  const overlayId = submissionId ?? undefined;

  const [result, setResult] = useState<StudyProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [modalKey, setModalKey] = useState<string | null>(null);
  const [rationale, setRationale] = useState('');

  // Worker-backed assembly (A2F-1366): POST enqueues study_profile_scan; useScanTask
  // polls GET /tasks/{id} for progress + the final StudyProfileResponse.
  const { taskState, startTask, cancel: cancelScan, result: scanResult } =
    useScanTask<StudyProfileResponse>('study_profile');
  const scanning = taskState.isScanning;

  const loadCached = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    try {
      const r = await api.studyProfile.get(projectId, overlayId);
      setResult(r);
      setScanned(r != null);
      setError(null);
    } catch (e) {
      const transient =
        e instanceof Error && /failed to fetch|networkerror|load failed/i.test(e.message);
      if (!transient) setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [projectId, overlayId]);

  const runScan = useCallback(async () => {
    if (!projectId) return;
    setError(null);
    try {
      const { taskId } = await api.studyProfile.scan(projectId, undefined, overlayId, true);
      startTask(taskId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    }
  }, [projectId, overlayId, startTask]);

  // Blank the view to a loading state whenever the submission focus changes, so switching
  // scope never flashes the previous submission's studies before the scoped read resolves.
  useEffect(() => {
    setResult(null);
    setScanned(false);
    setLoading(true);
  }, [overlayId]);

  // On mount: load cached + recover an in-flight scan (A2F-1252 pattern).
  useEffect(() => {
    void loadCached();
    if (!projectId) return;
    api.tasks
      .findRunning(projectId, 'study_profile_scan')
      .then((task) => { if (task?.status === 'running') startTask(task.taskId); })
      .catch(() => { /* no running scan — fine */ });
  }, [loadCached, projectId, startTask]);

  // Adopt the task result, then re-read so persisted dismissals merge in.
  useEffect(() => {
    if (!scanResult) return;
    setResult(scanResult);
    setScanned(true);
    void loadCached();
  }, [scanResult, loadCached]);

  const toggleRow = useCallback((id: string) => {
    setExpanded((m) => ({ ...m, [id]: !m[id] }));
  }, []);

  const confirmDismiss = useCallback(async () => {
    if (!projectId || !modalKey || rationale.trim().length < MIN_RATIONALE) return;
    try {
      await api.studyProfile.dismiss(projectId, modalKey, rationale.trim());
      setModalKey(null);
      setRationale('');
      await loadCached();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to dismiss');
    }
  }, [projectId, modalKey, rationale, loadCached]);

  const restoreFinding = useCallback(async (findingKey: string) => {
    if (!projectId) return;
    try {
      await api.studyProfile.undismiss(projectId, findingKey);
      await loadCached();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore');
    }
  }, [projectId, loadCached]);

  if (!activeProject) {
    return <div className="p-8 text-sm text-slate-500">Select a project to view the Study Profile.</div>;
  }

  const studies = result?.studies ?? [];
  const unreadableCount = result?.errors?.length ?? 0;
  // The specific documents behind an incomplete scan (truncated/unreadable), so the banner can
  // NAME them instead of a contentless "results may be partial" (A2F-1380 — recon now threads
  // its truncated docs up). De-duplicated, capped for display.
  const incompleteDocs = Array.from(new Set(
    (result?.errors ?? []).map((e) => String((e as Record<string, unknown>).document ?? '')).filter(Boolean),
  ));
  const scanIncomplete = result != null && (result.status === 'incomplete' || unreadableCount > 0);
  const rValid = rationale.trim().length >= MIN_RATIONALE;
  const orphans = result?.orphans;

  return (
    <div className="max-w-5xl mx-auto p-6 flex flex-col" style={{ rowGap: '18px' }}>
      {/* ===== Header ===== */}
      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex justify-between items-start gap-5">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-orange">Chrona Review</div>
            <h1 className="text-xl font-bold tracking-tight text-brand-charcoal mt-1">Study Profile</h1>
            <p className="text-[13px] leading-relaxed text-slate-500 mt-2 max-w-xl">
              One card per trial. Every row — design, enrollment, population — stays quiet when your documents
              agree and draws the eye when one drifted. Click a row to see the value each document states, its
              confidence, and the source sentence.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void runScan()}
            disabled={!canRunScans || scanning || loading}
            title={!canRunScans ? 'Requires Contributor role' : undefined}
            className="flex-none inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${scanning ? 'animate-spin' : ''}`} /> {scanned ? 'Re-scan' : 'Scan'}
          </button>
        </div>

        {/* status strip */}
        <div className="flex items-center gap-4 flex-wrap pt-4 border-t border-slate-100" style={{ marginTop: '18px' }}>
          {scanning ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                <LoaderCircle className="w-3 h-3 animate-spin" />
                {taskState.totalDocs > 0
                  ? `Scanning ${taskState.completedDocs}/${taskState.totalDocs}${taskState.currentDocument ? ` — ${taskState.currentDocument}` : ''}`
                  : 'Scanning'}
              </span>
              <button
                type="button"
                onClick={() => void cancelScan()}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-full px-3 py-1 hover:bg-slate-50"
              >
                <X className="w-3 h-3" /> Cancel
              </button>
            </>
          ) : taskState.stoppedEarly ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1">
              <X className="w-3 h-3" /> {taskState.stoppedMessage || 'Scan cancelled'}
            </span>
          ) : scanned && result?.stale ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              <Clock className="w-3 h-3" /> Showing last results
            </span>
          ) : scanned ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              <Check className="w-3 h-3" /> Scan complete
            </span>
          ) : null}
        </div>
      </section>

      {(error || taskState.error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error || taskState.error}</div>
      )}

      {/* out-of-date results (A2F-1281): the exact scan cache missed (a newer scanner version
          or a changed document set), so we're showing the last stored profile rather than a
          blank page. Non-blocking — the Re-scan button above refreshes. */}
      {result?.stale && !scanning && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800 flex items-start gap-2">
          <RotateCcw className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Showing your last results — they may be out of date because{' '}
            {result.staleReason === 'doc_set'
              ? 'the document set changed since this scan.'
              : 'the analysis was improved since this scan.'}{' '}
            <button
              type="button"
              onClick={() => void runScan()}
              disabled={!canRunScans}
              className="font-semibold underline underline-offset-2 hover:text-amber-900 disabled:no-underline disabled:opacity-60"
            >
              Re-scan to refresh
            </button>
            {!canRunScans && ' (requires Contributor role).'}
          </span>
        </div>
      )}

      {/* incomplete-scan honesty (A2F-1332/1310): never let unreadable docs read as a clean "agree" */}
      {scanIncomplete && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800 flex items-start gap-2">
          <EyeOff className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {incompleteDocs.length > 0 ? (
              <>
                {incompleteDocs.length} document{incompleteDocs.length === 1 ? ' was' : 's were'} only
                partially read (truncated or unreadable), so results are incomplete — a blank row here
                may not mean &ldquo;not stated.&rdquo;
                <span className="block mt-1 text-[12px] text-amber-700">
                  {incompleteDocs.slice(0, 4).join(' · ')}
                  {incompleteDocs.length > 4 ? ` · +${incompleteDocs.length - 4} more` : ''}
                </span>
              </>
            ) : (
              'This scan was incomplete, so a blank row here may not mean “not stated.”'
            )}
          </span>
        </div>
      )}

      {/* ===== Body ===== */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <LoaderCircle className="w-6 h-6 mx-auto animate-spin text-brand-orange" />
          <div className="mt-2 text-sm text-slate-500">Loading…</div>
        </div>
      ) : scanning ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center flex flex-col items-center gap-3.5">
          <LoaderCircle className="w-8 h-8 animate-spin text-brand-orange" />
          <div className="text-base font-semibold text-slate-900">Assembling study profiles…</div>
          <div className="max-w-md text-[13px] leading-relaxed text-slate-500">
            Reading the design across each study and comparing values across the submission.
          </div>
        </div>
      ) : !scanned ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <FlaskConical className="w-7 h-7 mx-auto text-slate-300" />
          <div className="mt-2 text-sm font-semibold text-slate-800">No scan has been run yet</div>
          <div className="mt-1 text-xs text-slate-500">
            {canRunScans ? 'Run a scan to assemble the study profiles.' : 'Ask a contributor to run a scan.'}
          </div>
          {canRunScans && (
            <button
              type="button"
              onClick={() => void runScan()}
              className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:opacity-90"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Run scan
            </button>
          )}
        </div>
      ) : studies.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <FlaskConical className="w-7 h-7 mx-auto text-slate-300" />
          <div className="mt-2 text-sm font-semibold text-slate-800">No studies found</div>
          <div className="mt-1 text-xs text-slate-500">
            No study-design facts were extracted from the clinical documents in this submission.
          </div>
        </div>
      ) : (
        <div className="flex flex-col" style={{ rowGap: '18px' }}>
          {studies.map((entity) => (
            <StudyCard
              key={entity.studyId}
              entity={entity}
              expanded={expanded}
              toggleRow={toggleRow}
              canDismiss={canDismissFindings}
              onDismiss={(key) => { setRationale(''); setModalKey(key); }}
              onRestore={(key) => void restoreFinding(key)}
            />
          ))}

          {/* orphan bucket — facts not attributable to a study (surfaced, never dropped) */}
          {orphans && orphans.count > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2">
                <FileQuestion className="w-4 h-4 text-slate-400" />
                <span className="text-[13px] font-semibold text-slate-700">
                  {orphans.count} fact{orphans.count === 1 ? '' : 's'} couldn&apos;t be attributed to a study
                </span>
              </div>
              <p className="text-[12px] text-slate-500 mt-1">
                Surfaced rather than dropped — these appeared in documents that reference more than one study (or none).
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(orphans.byFactType).map(([ft, items]) => (
                  <span key={ft} className="text-[11px] font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-0.5">
                    {ft} ×{items.length}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* parked product question: design-field drift direction deferral (file-header note) */}
      {scanned && studies.length > 0 && (
        <div className="text-[11px] leading-relaxed text-slate-400 border-t border-slate-100 pt-3">
          Design-field differences (e.g. blinding, control) are shown as non-directional — Chrona surfaces that
          sources differ but does not yet point to an authoritative one for design fields. Authority direction for
          these is deferred (engine work tracked under A2F-1299/A2F-1363); identifier drift is already directed.
        </div>
      )}

      {result && (
        <ScanFooter
          data={{
            scannedAt: result.scannedAt,
            modelUsed: 'Study design + reconciliation',
            totalFiles: result.totalFiles,
            errors: (result.errors ?? []).map((e, i) => ({
              document: String((e as Record<string, unknown>).document ?? '(unknown)'),
              message: String((e as Record<string, unknown>).message ?? ''),
              index: i,
            })),
          }}
        />
      )}

      {/* ===== Dismiss modal ===== */}
      {modalKey && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-6 z-[100]">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Study Profile · drift</div>
                <h2 className="text-lg font-bold leading-tight text-slate-900 mt-1">Dismiss finding</h2>
              </div>
              <button type="button" onClick={() => setModalKey(null)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[13px] leading-relaxed text-slate-600 mt-3.5">
              You are dismissing this feature drift. This is an audited decision — a written rationale is required and is
              retained in the submission&apos;s audit trail.
            </p>
            <div className="mt-3.5">
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Reason for dismissal (required, minimum {MIN_RATIONALE} characters)
              </label>
              <textarea
                rows={3}
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="e.g. Confirmed with clinical — the open-label extension is a distinct phase, not a blinding conflict."
                className="w-full border border-slate-200 rounded-lg p-3 text-[13px] leading-relaxed text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 resize-y"
                autoFocus
              />
              <div className="text-[11px] font-mono text-slate-400 mt-1.5">
                {rationale.trim().length} / {MIN_RATIONALE} characters minimum
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-lg">
              <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-[11px] leading-snug text-slate-500">Recorded as an attributable, time-stamped decision · 21 CFR Part 11</span>
            </div>
            <div className="flex gap-2.5 justify-end mt-4">
              <button
                type="button"
                onClick={() => setModalKey(null)}
                className="px-3.5 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDismiss()}
                disabled={!rValid}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Shield className="w-3.5 h-3.5" /> Dismiss with rationale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
